<script lang="ts" setup>
	import usePurchases from '~/services/apis/purchases-info'

	const props = defineProps<{
		emitForm?: boolean
		defaultPeriod?: string
		isRenewProcess?: boolean
	}>()
	const { currencyToNaira } = useCurrency()
	const { monthByNumber } = useUtils()
	const emits = defineEmits<{
		(event: 'submit', arg: unknown): void
	}>()

	const currentPlanPrice = shallowRef(0)
	const newPeriod = reactive({
		premium: 0,
		duration: '',
		per_person_price: 0
	})
	const loadingPeriod = shallowRef(false)
	const loadingPrice = shallowRef(false)
	const confirmModal = shallowRef(false)
	const { plan_id } = useRoute().params
	const { CoverPeriods } = useProducts()
	const { convertPeriodToMonths } = useUtils()
	const { computeEditCoverPeriod } = usePurchases()
	const {
		categoryType,
		coverPeriodLabel: currentCoverPeriod,
		totalStaffSlots,
		totalDependantSlot,
		categoryName,
		premium: oldPremium,
		provider,
		productId,
		calculatePricePerProduct
	} = usePurchaseInfo()
	const activeTab = ref('')
	const tabList = computed(() =>
		// eslint-disable-next-line vue/no-side-effects-in-computed-properties
		CoverPeriods.sort((a, b) => {
			if (a.value < b.value) {
				return -1
			}
			return 1
		}).filter(
			record =>
				provider.value === Providers.bastion &&
				record.value != 'annual' &&
				(categoryType.value.isHealth || categoryType.value.isTravel)
		)
	)
	const activePlanDetails = computed(() => {
		const cName =
			categoryType.value.isHealth || categoryType.value?.isTravel
				? 'Total Staff'
				: categoryType.value?.isAuto
					? 'Total vehicles'
					: `Total ${categoryName.value} gadgets`
		return {
			[cName]: totalStaffSlots.value,
			'Total Dependants': {
				value: totalDependantSlot.value,
				disabled: !categoryType.value.isHealth
			}
		}
	})
	const pricePerProduct = shallowRef(0)

	const getPlanPrice = async (cover_period: string) => {
		newPeriod.premium = 0
		return await resolve(async () => {
			const result = await computeEditCoverPeriod({
				month: String(monthByNumber.value[cover_period]),
				plan_id: String(plan_id),
				product_id: productId.value
			})
			if (result?.data) {
				newPeriod.premium = Number(result.data?.premium ?? 0)
				newPeriod.per_person_price = Number(result.data?.price_per_person)
				newPeriod.duration = cover_period
				pricePerProduct.value = calculatePricePerProduct(newPeriod.premium)
				return result.data?.current_price_per_person ?? result.data?.premium ?? 0
			}
			return '0'
		}, loadingPrice)
	}

	const confirmSwitch = () => {
		if (props.emitForm) {
			emits('submit', newPeriod.duration)
			return null
		}

		navigateTo({
			path: `/mycover/${plan_id}/edit-period-of-cover`,
			query: {
				info: encryptData({
					type: 'cover-period',
					page: 'summary',
					category: categoryName.value,
					plan_id: plan_id as string,
					coverPeriod: newPeriod.duration
				})
			}
		})
	}
	const handleSwitch = () => {
		confirmModal.value = !loadingPrice.value && !!newPeriod.premium
	}

	watch(
		categoryName,
		async () => {
			try {
				loadingPeriod.value = true
				loadingPrice.value = true
				activeTab.value = String(props.defaultPeriod || currentCoverPeriod.value)
				if (productId.value) {
					const planPrice = await getPlanPrice(activeTab.value || '')
					currentPlanPrice.value = Math.round(planPrice as number)
				}
			} finally {
				loadingPeriod.value = false
				loadingPrice.value = false
			}
		},
		{ immediate: true }
	)
</script>

<template>
	<div class="border border-t-transparent pb-5">
		<div class="flex w-full flex-col items-center justify-center bg-[#E7F9F3] px-3 py-5">
			<ui-select
				v-model="activeTab"
				:options="tabList"
				options-class="text-sm"
				trigger-class="bg-transparent border-transparent  w-auto mx-auto inline-flex capitalize"
				class="mx-auto"
				@change="e => getPlanPrice(e as string)"
			/>
		</div>

		<div class="mt-4 px-3">
			<div class="flex justify-center">
				<div
					v-if="!loadingPrice"
					class="inline-flex items-center justify-center rounded-lg border-purple-100 bg-purple-25 p-[8px_12px] text-sm font-medium"
				>
					{{ currencyToNaira(newPeriod.premium || currentPlanPrice) }} /
					{{ convertPeriodToMonths(activeTab) }}
				</div>
				<loader-skeleton v-else :count="1" :height="40" />
			</div>
			<ul class="mt-10">
				<li
					v-for="(item, k) in activePlanDetails"
					:key="k"
					class="flex w-full items-center justify-between py-3 text-sm text-gray-600"
					:class="{ hidden: typeof item === 'object' && item.disabled }"
				>
					<span> {{ k }}</span>
					<span v-if="typeof item === 'object'" class="font-medium">
						{{ item.value }}
					</span>

					<span v-else class="font-medium">
						{{ item }}
					</span>
				</li>
			</ul>

			<template v-if="activeTab === currentCoverPeriod">
				<ul>
					<li
						class="mt-3 flex items-center justify-between border-t-2 border-gray-300 py-3 pt-5 text-sm text-gray-600"
					>
						<span>Amount Paid</span>
						<span class="font-medium">{{ currencyToNaira(oldPremium) }}</span>
					</li>

					<li class="mt-14">
						<button
							type="button"
							class="default-btn w-full justify-center bg-gray-200 text-gray-500"
							disabled
						>
							Current Cover period
						</button>
					</li>
				</ul>
			</template>

			<template v-else>
				<div class="mt-20" />
				<shared-highlight-note
					v-if="isRenewProcess"
					icon-class="hidden"
					class="border-warning-100 bg-warning-25"
				>
					<span class="font-semibold text-blue-950">Note: </span>
					Your renewal will now be on a
					<span class="font-semibold capitalize text-blue-950">{{ activeTab }}</span> basis. Going forward,
					you'll pay {{ currencyToNaira(pricePerProduct) }} / person
					<span class="font-semibold capitalize text-blue-950">{{ activeTab }}</span
					>.
				</shared-highlight-note>
				<ui-button
					name="switch Cover period"
					:disabled="loadingPrice || !newPeriod.premium"
					:loading="loadingPrice"
					data-testid="switch cover period"
					class="default-btn mt-[38px] w-full justify-center bg-primary-2 !font-medium capitalize tracking-wide"
					:text="`Switch to ${activeTab}`"
					@click="handleSwitch"
				/>
			</template>
		</div>

		<dialogs-error
			v-model:show="confirmModal"
			type="warning"
			header="Cover Period"
			wrapper-class="bg-gray-50 w-full rounded-md py-8 px-5 min-h-[150px]"
			modal-button-text="Continue"
			@done="confirmSwitch"
		>
			<template #paragraph>
				<div class="leading- mt-5 font-medium tracking-wide">
					By changing your cover period, your total premium will change from
					<span class="font-medium text-primary-2">{{ currencyToNaira(currentPlanPrice) }}</span>
					{{ currentCoverPeriod }}, to
					<span class="font-medium text-primary-2">{{
						currencyToNaira(newPeriod.premium ?? newPeriod.per_person_price)
					}}</span>
					{{ newPeriod.duration }}.
					<span v-show="!emitForm">
						The new premium will also become active and charged upon your next renewal.
					</span>
				</div>
			</template>
		</dialogs-error>
	</div>
</template>
